import { useMemo, useState, type ReactNode } from 'react';
import { ArrowDownFromLine, ArrowUpToLine, MapPin, Pencil, Trash2, X } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/hooks/useI18n';
import { useLibraryScope } from '@/lib/library/hooks/useLibraryScope';
import {
  useDeleteActivityFromLibrary,
  useDemoteActivityFromAgency,
  usePromoteActivityToAgency,
  useSaveActivityToLibrary,
} from '@/lib/library/hooks/useActivityLibrary';
import {
  LIBRARY_ACTIVITY_STORAGE_ID,
  LibraryActivity,
  LibraryActivityFactory,
} from '@/lib/library/models/library-activity.model';
import { ActivityType, ItineraryActivity } from '@/lib/builder/models/activity.model';
import { getActivityModal, getActivityTypeIcon, getActivityTypeLabel } from '@/lib/builder/models/activity-configs.model';
import { ModalProps } from '@/lib/builder/models/activities-modal.model';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface LibraryActivitiesTableProps {
  /**
   * Tipo di attività su cui filtrare la libreria (accommodation, activity, flight, ...).
   * `null` = nessun filtro, mostra tutti i tipi (coerente con la select a singola
   * scelta in LibraryPage).
   */
  selectedActivityType: ActivityType | null;
  /**
   * Testo di ricerca corrente. Lo stato vive in `LibraryPage` (l'input sta a fianco
   * della select tipologia); qui viene solo applicato al filtro client-side.
   */
  search: string;
  /**
   * Attività di libreria già fetchate a livello di pagina (`LibraryPage`, una sola
   * chiamata `useActivityLibraryQuery()` senza filtri, eventualmente già ristrette
   * per owner scope se l'utente ha un'agenzia). Questo componente si occupa solo del
   * filtro client-side per tipo attività, tag, ricerca testuale e (per l'admin) ownerScope.
   */
  activities: LibraryActivity[];
  isLoading: boolean;
  /**
   * Se true mostra su ogni riga non ancora promossa (agency_id null) l'azione
   * "Promuovi in libreria agenzia". Ha senso solo per il membro agenzia che sta
   * consultando la propria libreria personale (vedi `useLibraryScope().canPromote`).
   */
  enablePromote?: boolean;
  /**
   * Se true mostra su ogni riga già in agenzia (agency_id valorizzato) di cui l'utente
   * corrente è il proprietario (creatore) l'azione "Rimuovi dalla libreria agenzia",
   * che riporta la riga a sola personale (agency_id = null). Ha senso nella vista
   * libreria agenzia (vedi `LibraryPage`, scope 'agency').
   */
  enableDemote?: boolean;
  /**
   * Se mostrare la colonna "Proprietario". In libreria personale (scope 'user') è
   * ridondante — le righe sono sempre dell'utente corrente — quindi va nascosta;
   * ha senso solo in libreria agenzia (più membri) o per l'admin.
   */
  showOwner?: boolean;
}

type OwnerScopeFilter = 'all' | 'user' | 'agency';

/**
 * Iniziali del proprietario per il pallino avatar: iniziale nome + iniziale cognome,
 * con fallback sulla prima lettera dell'email (o '?') se il nome non è disponibile.
 */
function getOwnerInitials(firstName?: string | null, lastName?: string | null, email?: string | null): string {
  const initials = `${firstName?.trim()?.[0] ?? ''}${lastName?.trim()?.[0] ?? ''}`.toUpperCase();
  if (initials) return initials;
  return (email?.trim()?.[0] ?? '?').toUpperCase();
}

/**
 * Tabella "Libreria": filtra client-side le attività già fetchate dal genitore
 * (nessuna chiamata di rete qui), con filtro per tipo attività (select a singola
 * scelta, gestita in LibraryPage), ricerca testuale, filtro per tag
 * e azioni di modifica/eliminazione riga.
 */
export function LibraryActivitiesTable({ selectedActivityType, search, activities, isLoading, enablePromote = false, enableDemote = false, showOwner = true }: LibraryActivitiesTableProps) {
  const { t } = useI18n();
  const { user, userRole } = useAuth();
  const { isAgencyAccount } = useLibraryScope();
  const isAdmin = userRole === 'admin';

  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [ownerScope, setOwnerScope] = useState<OwnerScopeFilter>('all');
  const [deleteTarget, setDeleteTarget] = useState<LibraryActivity | null>(null);

  const deleteMutation = useDeleteActivityFromLibrary();
  const saveActivityToLibrary = useSaveActivityToLibrary();
  const promoteActivityToAgency = usePromoteActivityToAgency();
  const demoteActivityFromAgency = useDemoteActivityFromAgency();

  // Click riga: apre la stessa modale del builder in sola lettura (`readOnly`),
  // prefillata coi dati della entry. Matita: stessa modale, ma modificabile —
  // al salvataggio aggiorna la riga esistente (stesso id/tag/owner, nessuna
  // nuova scelta di tag/ambito come nel flusso "Aggiungi" di LibraryPage).
  const [viewActivity, setViewActivity] = useState<LibraryActivity | null>(null);
  const [editActivity, setEditActivity] = useState<LibraryActivity | null>(null);

  const closeViewModal = () => setViewActivity(null);
  const closeEditModal = () => setEditActivity(null);

  const handleEditSave = (activity: Partial<ItineraryActivity>) => {
    if (!editActivity) return;
    const payload = LibraryActivityFactory.fromItineraryActivity(activity as ItineraryActivity, {
      id: editActivity.id,
      tags: editActivity.tags,
    });
    // In modifica preserviamo l'appartenenza corrente della riga: passiamo il suo
    // `agency_id` così l'upsert non sposta la entry tra personale e agenzia.
    saveActivityToLibrary.mutate(
      {
        activity: payload,
        agencyId: editActivity.agency_id,
      },
      { onSuccess: () => setEditActivity(null) }
    );
  };

  const activitiesForType = useMemo(
    () =>
      selectedActivityType === null
        ? activities
        : activities.filter((activity) => activity.activity_type === selectedActivityType),
    [activities, selectedActivityType]
  );

  // Replica client-side lo stesso match testuale della RPC get_activity_library
  // (ILIKE case-insensitive su title/description/address), ora eseguito in memoria
  // sul dataset già fetchato invece che con una chiamata server per ogni carattere digitato.
  // Nota: `location` NON è un indirizzo/luogo in questo modello — è l'indice d'ordine
  // dell'attività nel giorno (vedi itinerary-builder.model.ts `addActivity`), quindi
  // il campo mostrato/cercato come "luogo" è `address`, non `location`.
  const searchedActivities = useMemo(() => {
    const searchLower = search.trim().toLowerCase();
    if (!searchLower) return activitiesForType;
    return activitiesForType.filter(
      (activity) =>
        activity.title.toLowerCase().includes(searchLower) ||
        (activity.description ?? '').toLowerCase().includes(searchLower) ||
        (activity.address ?? '').toLowerCase().includes(searchLower)
    );
  }, [activitiesForType, search]);

  const availableTags = useMemo(() => {
    const tagSet = new Set<string>();
    searchedActivities.forEach((activity) => activity.tags.forEach((tag) => tagSet.add(tag)));
    return Array.from(tagSet).sort();
  }, [searchedActivities]);

  const filteredActivities = useMemo(() => {
    return searchedActivities.filter((activity) => {
      if (selectedTags.length > 0 && !selectedTags.some((tag) => activity.tags.includes(tag))) return false;
      // Filtro extra disponibile solo per gli admin: la RLS SELECT già espone loro
      // tutte le righe, qui si aggiunge solo un narrowing lato UI. Nel nuovo modello
      // "agenzia" = riga con `agency_id` valorizzato, "personale" = `agency_id` null.
      if (isAdmin && ownerScope === 'agency' && activity.agency_id === null) return false;
      if (isAdmin && ownerScope === 'user' && activity.agency_id !== null) return false;
      return true;
    });
  }, [searchedActivities, selectedTags, isAdmin, ownerScope]);

  /**
   * Replica lato UI lo stesso vincolo già imposto dalla RLS UPDATE/DELETE
   * (migration 20260713110013/20260713110015): non sostituisce la sicurezza server-side,
   * serve solo a nascondere azioni che il server rifiuterebbe comunque.
   */
  const canManage = (activity: LibraryActivity): boolean => {
    if (isAdmin) return true;
    if (!user) return false;
    // Gestibile se ne sono il creatore, oppure se sono l'account agenzia proprietario
    // della riga (agency_id = mio user id).
    return activity.owner_id === user.id || (isAgencyAccount && activity.agency_id === user.id);
  };

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSettled: () => setDeleteTarget(null),
    });
  };

  const columns: DataTableColumn<LibraryActivity>[] = [
    {
      id: 'thumbnail',
      header: '',
      headClassName: 'w-16',
      cell: (activity) => {
        const thumbnail = (activity.media as unknown as string[] | null | undefined)?.[0];
        return (
          <div className="w-10 h-10 rounded-md bg-gray-100 overflow-hidden flex-shrink-0 flex items-center justify-center text-gray-300">
            {thumbnail ? (
              <img
                src={thumbnail}
                alt={activity.title}
                className="w-full h-full object-cover"
              />
            ) : (
              getActivityTypeIcon(activity.activity_type)
            )}
          </div>
        );
      },
    },
    {
      id: 'title',
      header: t('library.columnTitle'),
      // Nessuna larghezza: in una <table table-fixed> è l'unica colonna senza width,
      // quindi assorbe tutto lo spazio residuo (thumbnail e azioni sono a larghezza
      // fissa, le altre a percentuale). È la colonna "flex-grow" della tabella.
      cellClassName: 'font-medium break-words',
      cell: (activity) => activity.title,
    },
    {
      id: 'type',
      header: t('library.columnType'),
      headClassName: 'w-[10%]',
      cell: (activity) => (
        <span className="flex items-center gap-1.5 text-sm text-gray-600 break-words">
          {getActivityTypeIcon(activity.activity_type)}
          {t(getActivityTypeLabel(activity.activity_type) as any)} {/* eslint-disable-line @typescript-eslint/no-explicit-any */}
        </span>
      ),
    },
    {
      id: 'tags',
      header: t('library.columnTags'),
      headClassName: 'w-[20%]',
      cell: (activity) =>
        activity.tags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {activity.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs text-gray-500 bg-gray-100 rounded px-1.5 py-0.5"
              >
                {tag}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-muted-foreground italic">-</span>
        ),
    },
    {
      id: 'location',
      header: t('library.columnLocation'),
      headClassName: 'w-[20%]',
      cell: (activity) =>
        activity.address ? (
          <span className="flex items-start gap-1 text-sm text-gray-600 break-words">
            <MapPin className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
            {activity.address}
          </span>
        ) : (
          <span className="text-muted-foreground italic">-</span>
        ),
    },
    {
      id: 'owner',
      header: t('library.columnOwner'),
      headClassName: 'w-[10%] text-center',
      cellClassName: 'text-center',
      // Proprietario (creatore) della riga: per non affollare la tabella mostriamo solo
      // un pallino con le iniziali; nome+cognome+email compaiono nel tooltip all'hover.
      // L'id (`owner_id`) non è mostrato ma resta disponibile in `activity` per un futuro
      // link alla pagina pubblica dell'utente. I campi owner_* arrivano solo da
      // get_activity_library (join server-side): se assenti mostriamo un placeholder.
      cell: (activity) => {
        const fullName = [activity.owner_first_name, activity.owner_last_name]
          .filter(Boolean)
          .join(' ')
          .trim();
        if (!fullName && !activity.owner_email) {
          return <span className="text-muted-foreground italic">-</span>;
        }
        const initials = getOwnerInitials(
          activity.owner_first_name,
          activity.owner_last_name,
          activity.owner_email
        );
        // Se la riga è di proprietà dell'utente corrente coloriamo il pallino di verde
        // per evidenziare a colpo d'occhio i record propri (utile soprattutto in libreria
        // agenzia, dove convivono attività di più membri).
        const isOwn = !!user && activity.owner_id === user.id;
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className={`mx-auto flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium select-none cursor-default ${
                  isOwn ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                }`}
                aria-label={fullName || activity.owner_email || ''}
              >
                {initials}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-sm font-normal">
              <span className="flex flex-col">
                {fullName && <span className="font-medium text-popover-foreground">{fullName}</span>}
                {activity.owner_email && (
                  <span className="text-xs text-muted-foreground">{activity.owner_email}</span>
                )}
                {isOwn && (
                  <span className="mt-0.5 text-xs font-medium text-green-600">
                    {t('library.ownerYouHint')}
                  </span>
                )}
              </span>
            </TooltipContent>
          </Tooltip>
        );
      },
    },
  ].filter((column) => column.id !== 'owner' || showOwner);

  return (
    <>
      <div className="flex flex-col min-h-0 h-full">
        <div className="shrink-0 space-y-4">
        {availableTags.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {availableTags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => toggleTag(tag)}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                  selectedTags.includes(tag)
                    ? "bg-gray-900 text-white border-gray-900"
                    : "border-gray-200 text-gray-600 hover:bg-gray-50"
                }`}
              >
                {tag}
              </button>
            ))}
            {selectedTags.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-gray-500 hover:text-gray-700"
                onClick={() => setSelectedTags([])}
              >
                <X className="h-3.5 w-3.5 mr-1" />
                {t("library.clearTagsFilter")}
              </Button>
            )}
          </div>
        )}
        {isAdmin && (
          <div className="flex sm:justify-end">
            <Select
              value={ownerScope}
              onValueChange={(value) =>
                setOwnerScope(value as OwnerScopeFilter)
              }
            >
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder={t("library.ownerScopeLabel")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t("library.ownerScopeAll")}
                </SelectItem>
                <SelectItem value="user">
                  {t("library.ownerScopeUser")}
                </SelectItem>
                <SelectItem value="agency">
                  {t("library.ownerScopeAgency")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        </div>

        <div className="flex-1 min-h-0 mt-4">
          <DataTable<LibraryActivity>
            data={filteredActivities}
            columns={columns}
            getRowId={(a) => a.id}
            onRowClick={(a) => setViewActivity(a)}
            actionsHeader={t("common.actions")}
            actionsHeadClassName="w-32"
            renderActions={(activity) => {
              const manageable = canManage(activity);
              // Il membro agenzia può promuovere in libreria agenzia solo le proprie
              // attività personali non ancora promosse (agency_id null).
              const promotable = enablePromote && activity.agency_id === null;
              // Simmetrico: il proprietario (creatore) di una riga già in agenzia può
              // rimuoverla dalla libreria agenzia e renderla di nuovo solo personale.
              const demotable =
                enableDemote &&
                activity.agency_id !== null &&
                !!user &&
                activity.owner_id === user.id;
              return (
                <div className="flex items-center justify-end gap-1">
                  {promotable && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      title={t('library.promoteToAgencyAction')}
                      aria-label={t('library.promoteToAgencyAction')}
                      disabled={promoteActivityToAgency.isPending}
                      onClick={(e) => {
                        e.stopPropagation();
                        promoteActivityToAgency.mutate(activity.id);
                      }}
                    >
                      <ArrowUpToLine className="h-4 w-4" />
                    </Button>
                  )}
                  {demotable && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      title={t('library.demoteFromAgencyAction')}
                      aria-label={t('library.demoteFromAgencyAction')}
                      disabled={demoteActivityFromAgency.isPending}
                      onClick={(e) => {
                        e.stopPropagation();
                        demoteActivityFromAgency.mutate(activity.id);
                      }}
                    >
                      <ArrowDownFromLine className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    disabled={!manageable}
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditActivity(activity);
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                    disabled={!manageable}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(activity);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              );
            }}
            isLoading={isLoading}
            emptyState={t("library.pickerEmptyState")}
          />
        </div>
      </div>

      {deleteTarget && (
        <AlertDialog
          open={!!deleteTarget}
          onOpenChange={(open) => !open && setDeleteTarget(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("library.deleteConfirmTitle")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("library.deleteConfirmDescription")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteConfirm}
                className="bg-red-600 hover:bg-red-700"
              >
                {t("common.delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {(() => {
        const activeActivity = editActivity ?? viewActivity;
        if (!activeActivity) return null;
        const isEditing = editActivity !== null;
        return getActivityModal(activeActivity.activity_type)({
          type: activeActivity.activity_type,
          isOpen: true,
          onClose: isEditing ? closeEditModal : closeViewModal,
          onSave: isEditing ? handleEditSave : () => {},
          icon: getActivityTypeIcon(activeActivity.activity_type),
          initialData: activeActivity.toItineraryActivity(),
          itineraryId: LIBRARY_ACTIVITY_STORAGE_ID,
          hideDates: true,
          readOnly: !isEditing,
        } as ModalProps) as ReactNode;
      })()}
    </>
  );
}
